import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../../../../components/table";
import type { NotificationItem, NotificationSortDirection, NotificationSortKey } from "../../types/notification.types";
import type { PublicOption } from "../../../common/public-options/publicOptions.api";
import {
  buildNotificationTableColumns,
  type NotificationTableFilters,
} from "./notificationTableColumns";

type NotificationDataTableProps = {
  notifications: NotificationItem[];
  alertTypes: PublicOption[];
  filters: NotificationTableFilters;
  sortKey: NotificationSortKey | null;
  sortDirection: NotificationSortDirection | null;
  onFilterChange: (key: keyof NotificationTableFilters, value: string) => void;
  onSort: (key: NotificationSortKey, direction: NotificationSortDirection | null) => void;
  loading?: boolean;
  error: string | null;
  onRetry: () => void | Promise<void>;
  emptyLabel?: string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRowClick?: (notification: NotificationItem) => void;
  updatingIds: ReadonlySet<string>;
  formatDate: (date: string) => string;
  isOlderThan24Hours: (date: string) => boolean;
  onEdit: (notification: Pick<NotificationItem, "id" | "title">) => void;
  onPinToggle: (notification: NotificationItem) => void | Promise<void>;
  onDelete: (notification: Pick<NotificationItem, "id" | "title">) => void;
};

export function NotificationDataTable({
  notifications,
  alertTypes,
  filters,
  sortKey,
  sortDirection,
  onFilterChange,
  onSort,
  loading,
  error,
  onRetry,
  emptyLabel,
  page,
  totalPages,
  onPageChange,
  onRowClick,
  updatingIds,
  formatDate,
  isOlderThan24Hours,
  onEdit,
  onPinToggle,
  onDelete,
}: NotificationDataTableProps) {
  const { t } = useTranslation();
  const alertTypeOptions = useMemo(
    () =>
      alertTypes
        .filter((alertType, index, items) => index === items.findIndex(({ value }) => value === alertType.value))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [alertTypes],
  );

  const columns = useMemo(
    () =>
      buildNotificationTableColumns({
        t,
        filters,
        alertTypeOptions,
        sortKey,
        sortDirection,
        onFilterChange,
        onSort,
        updatingIds,
        formatDate,
        isOlderThan24Hours,
        onEdit,
        onPinToggle,
        onDelete,
      }),
    [
      alertTypeOptions,
      filters,
      formatDate,
      isOlderThan24Hours,
      onEdit,
      onDelete,
      onPinToggle,
      onFilterChange,
      onSort,
      sortDirection,
      sortKey,
      t,
      updatingIds,
    ],
  );

  return (
    <DataTable
      columns={columns}
      dataSource={notifications}
      rowKey="id"
      loading={loading}
      loadingLabel={t("notification.loading_notifications")}
      error={error}
      onRetry={() => void onRetry()}
      emptyLabel={emptyLabel ?? "Currently no notifications available."}
      containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
      tableClassName="min-w-max"
      onRowClick={onRowClick ? (notification) => onRowClick(notification) : undefined}
      pagination={{ page, totalPages, onPageChange }}
    />
  );
}
